// Pure helpers for the File Browser: row normalization (new FileRow and the
// legacy /v1/files/recent + /v1/vfs/folders shapes), folder tree indexing,
// labels and formatting.
import { libraryKind, LIBRARY_KIND_LABELS } from '../atlas/viewer/fileKinds';

export const SOURCE_GROUP_LABELS = {
  uploaded: 'Uploaded',
  generated: 'Generated',
  email: 'Email',
  research: 'Web research',
  other: 'Other',
};

// Short chip shown on a row / recent card.
export const SOURCE_GROUP_CHIPS = {
  uploaded: 'Uploaded',
  generated: 'Atlas',
  email: 'Email',
  research: 'Research',
  other: 'Other',
};

// Same mapping as the backend's source_group (for legacy rows that lack it).
const SOURCE_TO_GROUP = {
  upload: 'uploaded',
  attach: 'uploaded',
  save_file: 'generated',
  generate_image: 'generated',
  generated: 'generated',
  viz_to_image: 'generated',
  land_data: 'generated',
  email_attachment: 'email',
  web_research: 'research',
  web_image_download: 'research',
};

export const KIND_FILTERS = [
  { id: 'pdf', label: 'PDF' },
  { id: 'spreadsheet', label: 'Spreadsheets' },
  { id: 'document', label: 'Docs' },
  { id: 'presentation', label: 'Slides' },
  { id: 'image', label: 'Images' },
];

export const SORT_OPTIONS = [
  { id: 'recent', label: 'Most recent' },
  { id: 'name', label: 'Name' },
  { id: 'size', label: 'Size' },
];

export const PAGE_SIZE = 50;

export function sourceGroupOf(file) {
  if (file?.source_group && SOURCE_GROUP_LABELS[file.source_group]) return file.source_group;
  return SOURCE_TO_GROUP[String(file?.source || '').toLowerCase()] || 'other';
}

export function sourceLabel(file) {
  const group = sourceGroupOf(file);
  if (group === 'generated') return 'Generated by Atlas';
  if (group === 'email') return 'Email attachment';
  if (group === 'research') return 'Saved from web research';
  if (group === 'uploaded') return 'Uploaded';
  return 'Added to the library';
}

export function kindLabelOf(file) {
  return LIBRARY_KIND_LABELS[libraryKind(file)] || 'File';
}

function legacyFolders(raw) {
  const paths = Array.isArray(raw?.folder_paths) && raw.folder_paths.length
    ? raw.folder_paths
    : (raw?.folder_path ? [raw.folder_path] : []);
  return paths
    .filter((p) => typeof p === 'string' && p.trim())
    .map((p) => {
      const parts = p.split(' / ');
      return { folder_id: null, name: parts[parts.length - 1], path: p };
    });
}

/**
 * FileRow.suggestion: where Atlas thinks an unfiled file belongs, or null.
 * new_folders non-empty means accepting creates those folders; path is the
 * full resulting path either way.
 */
export function normalizeSuggestion(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const newFolders = Array.isArray(raw.new_folders) ? raw.new_folders.filter((n) => typeof n === 'string' && n.trim()) : [];
  const path = typeof raw.path === 'string' && raw.path.trim() ? raw.path.trim() : newFolders.join(' / ');
  if (!path) return null;
  return {
    folder_id: raw.folder_id || null,
    path,
    new_folders: newFolders,
    base_parent_id: raw.base_parent_id || null,
    reason: typeof raw.reason === 'string' && raw.reason.trim() ? raw.reason.trim() : null,
    suggested_at: raw.suggested_at || null,
  };
}

/** The suggestion to offer for a row: only while the file is in no folder. */
export function activeSuggestion(file) {
  if (!file?.suggestion) return null;
  if (Array.isArray(file.folders) && file.folders.length) return null;
  return file.suggestion;
}

/** Why accepting a suggestion failed, as the end of "Couldn't move X: …". */
export function suggestionErrorText(err) {
  if (err?.code === 'suggested_folder_gone') return 'the suggested folder no longer exists';
  if (err?.code === 'no_suggestion') return 'Atlas no longer has a suggestion for it';
  return err?.message || 'move failed';
}

/** Any file row the API returns, as a FileRow the UI can rely on. */
export function normalizeFileRow(raw) {
  if (!raw || !raw.file_id) return null;
  const folders = Array.isArray(raw.folders)
    ? raw.folders.filter(Boolean).map((f) => ({
      folder_id: f.folder_id || null,
      name: f.name || '',
      path: f.path || f.name || '',
    }))
    : legacyFolders(raw);
  const row = {
    ...raw,
    filename: raw.filename || 'Untitled',
    created_at: raw.created_at || raw.uploaded_at || raw.updated_at || null,
    source_group: sourceGroupOf(raw),
    kind: libraryKind(raw),
    version_number: Number(raw.version_number) || 1,
    processing_status: String(raw.processing_status || raw.status || 'ready').toLowerCase(),
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    folders,
  };
  // Absent (an older backend) stays absent: only an explicit null means
  // "no suggestion yet", which is what asks Atlas for one.
  if ('suggestion' in raw) row.suggestion = normalizeSuggestion(raw.suggestion);
  return row;
}

export function processingInfo(file) {
  const status = String(file?.processing_status || 'ready').toLowerCase();
  if (status === 'error') return { busy: false, error: true, label: 'Processing failed' };
  if (status === 'pending' || status === 'queued') return { busy: true, error: false, label: 'Queued' };
  if (status === 'processing') return { busy: true, error: false, label: 'Processing' };
  return { busy: false, error: false, label: '' };
}

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDate(iso, { withTime = false } = {}) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const opts = { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) };
  if (withTime) Object.assign(opts, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleString('en-US', opts);
}

/** "now", "5m", "2h", "3d", "1w", "4mo", "2y": the compact age used in the Home list. */
export function timeAgo(iso, now = Date.now()) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.floor((now - then) / 60000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

export function pluralize(n, word, plural = `${word}s`) {
  return `${n} ${n === 1 ? word : plural}`;
}

export function splitFilename(filename) {
  const name = String(filename || '');
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
}

/** Same normalization the backend uses for folder-name uniqueness. */
export function normalizeFolderName(name) {
  let value = String(name || '');
  try { value = value.normalize('NFC'); } catch { /* old engines */ }
  return value.toLowerCase().replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '');
}

/**
 * Index the flat folder list from /v1/vfs/tree.
 *   byId, children (parent id or '' -> sorted folders), pathOf(id), descendantsOf(id),
 *   hintOf(id): extra text that tells same-named folders apart.
 */
export function buildFolderIndex(folders) {
  const list = Array.isArray(folders) ? folders.filter((f) => f?.folder_id) : [];
  const byId = new Map(list.map((f) => [f.folder_id, f]));
  const children = new Map();
  list.forEach((f) => {
    const parent = f.parent_folder_id && byId.has(f.parent_folder_id) ? f.parent_folder_id : '';
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(f);
  });
  children.forEach((items) => items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })));

  const pathCache = new Map();
  const pathOf = (id) => {
    if (!id || !byId.has(id)) return '';
    if (pathCache.has(id)) return pathCache.get(id);
    const folder = byId.get(id);
    let path = typeof folder.path === 'string' && folder.path.trim() ? folder.path : '';
    if (!path) {
      const names = [];
      const seen = new Set();
      let current = folder;
      while (current && !seen.has(current.folder_id)) {
        seen.add(current.folder_id);
        names.unshift(current.name || 'Untitled');
        current = current.parent_folder_id ? byId.get(current.parent_folder_id) : null;
      }
      path = names.join(' / ');
    }
    pathCache.set(id, path);
    return path;
  };

  const descendantsOf = (id) => {
    const out = new Set();
    const stack = [...(children.get(id) || [])];
    while (stack.length) {
      const next = stack.pop();
      if (out.has(next.folder_id)) continue;
      out.add(next.folder_id);
      stack.push(...(children.get(next.folder_id) || []));
    }
    return out;
  };

  // Same-named folders: show where each one lives (or, for siblings, how big
  // each one is) so they can be told apart and merged.
  const byName = new Map();
  list.forEach((f) => {
    const key = normalizeFolderName(f.name);
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(f);
  });
  const hints = new Map();
  byName.forEach((group) => {
    if (group.length < 2) return;
    group.forEach((f) => {
      const parentPath = f.parent_folder_id && byId.has(f.parent_folder_id) ? pathOf(f.parent_folder_id) : 'Top level';
      const siblingsSameName = group.filter((g) => (g.parent_folder_id || '') === (f.parent_folder_id || '')).length > 1;
      const count = Number(f.file_count) || 0;
      hints.set(f.folder_id, siblingsSameName
        ? `in ${parentPath} · ${pluralize(count, 'file')}`
        : `in ${parentPath}`);
    });
  });

  return {
    byId,
    children,
    roots: children.get('') || [],
    pathOf,
    descendantsOf,
    hintOf: (id) => hints.get(id) || '',
    hasDuplicateName: (id) => hints.has(id),
  };
}

export function folderBadge(folder) {
  const by = String(folder?.created_by || '').toLowerCase();
  if (by === 'agent') return 'Atlas';
  if (by === 'share_to_company') return 'Shared';
  if (by === 'system' || folder?.is_system) return 'System';
  return '';
}

// --- legacy fallbacks (backend without /v1/vfs/files) ---

export function matchesQuery(file, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [
    file.filename, file.summary, file.condensed_summary, file.file_category,
    ...(file.tags || []), ...(file.key_entities || []),
  ].filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

export function filterLegacyRows(rows, { q, sourceGroup, kind, unfiled } = {}) {
  return rows.filter((file) => {
    if (sourceGroup && sourceGroup !== 'all' && !String(sourceGroup).split(',').includes(file.source_group)) return false;
    if (kind && kind !== 'all' && file.kind !== kind) return false;
    if (unfiled && file.folders.length > 0) return false;
    return matchesQuery(file, q);
  });
}

export function sortRows(rows, sort) {
  const copy = [...rows];
  if (sort === 'name') {
    copy.sort((a, b) => String(a.filename).localeCompare(String(b.filename), undefined, { sensitivity: 'base', numeric: true }));
  } else if (sort === 'size') {
    copy.sort((a, b) => (Number(b.size_bytes) || 0) - (Number(a.size_bytes) || 0));
  } else {
    copy.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }
  return copy;
}

/** True when the route itself is missing (older backend), not "folder not found". */
export function isMissingEndpoint(err) {
  if (!err || (err.status !== 404 && err.status !== 405)) return false;
  if (err.status === 405) return true;
  const detail = err.body?.detail;
  return detail === 'Not Found' || detail === 'Method Not Allowed';
}

// --- Local edits shown before the server catches up ("overrides") ---
//
// A rename / move / delete patches the rows on screen right away. Each patched
// field remembers when it was made (a sequence number). Every row remembers
// when its data was requested (_fetchSeq). A patch only applies to rows
// fetched BEFORE it, so fresh server data always wins and a stale patch (an
// old folder chip after that folder was renamed or merged) never sticks.

let librarySeq = 0;
export function nextLibrarySeq() {
  librarySeq += 1;
  return librarySeq;
}

/** overrides[fileId][field] = { value, seq } */
export function addOverride(overrides, fileId, patch, seq = nextLibrarySeq()) {
  const current = overrides[fileId] || {};
  const next = { ...current };
  Object.entries(patch).forEach(([field, value]) => { next[field] = { value, seq }; });
  return { ...overrides, [fileId]: next };
}

/** The row as the user should see it, or null when it was deleted after it was fetched. */
export function applyOverride(row, overrides) {
  const entry = overrides?.[row.file_id];
  if (!entry) return row;
  const fetchedAt = Number(row._fetchSeq) || 0;
  let out = row;
  Object.entries(entry).forEach(([field, { value, seq }]) => {
    if (seq <= fetchedAt) return;
    if (out === row) out = { ...row };
    out[field] = value;
  });
  return out.deleted ? null : out;
}

export function applyOverrides(rows, overrides) {
  if (!overrides || !Object.keys(overrides).length) return rows;
  return rows.map((row) => applyOverride(row, overrides)).filter(Boolean);
}

/** Drop patch fields that fresh rows (fetched at fetchSeq) already supersede. */
export function pruneOverrides(overrides, fileIds, fetchSeq) {
  let changed = false;
  const next = { ...overrides };
  fileIds.forEach((id) => {
    const entry = next[id];
    if (!entry) return;
    const kept = Object.fromEntries(Object.entries(entry).filter(([, f]) => f.seq > fetchSeq));
    if (Object.keys(kept).length === Object.keys(entry).length) return;
    changed = true;
    if (Object.keys(kept).length) next[id] = kept; else delete next[id];
  });
  return changed ? next : overrides;
}

// --- Rename helpers ---

/** "Report.pdf" typed for a .pdf file -> "Report" (the extension is added back once). */
export function stripOwnExtension(base, ext) {
  const value = String(base || '').trim();
  if (!ext) return value;
  return value.toLowerCase().endsWith(ext.toLowerCase()) ? value.slice(0, -ext.length).trim() : value;
}

/** True when the server renamed to "<requested> (n)" because the name was taken. */
export function isDedupedName(requested, final) {
  const a = splitFilename(requested);
  const b = splitFilename(final);
  if (a.ext.toLowerCase() !== b.ext.toLowerCase()) return false;
  const escaped = a.base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped} \\(\\d+\\)$`).test(b.base);
}
